/* eslint-disable @typescript-eslint/no-extraneous-class */
import { OktaAuth, isAccessToken } from '@okta/okta-auth-js'
import { EventBus } from '../common'
import { RegionalConfig } from '../types'

export class AuthProvider {
  public regionConfig: RegionalConfig
  private oktaAuth: OktaAuth

  constructor(regionConfig: RegionalConfig) {
    this.regionConfig = regionConfig
    this.oktaAuth = new OktaAuth(regionConfig.okta)
  }

  public configure = async (): Promise<void> => {    
    // Handle callback - this needs to run first, before anything else. Required for SPAs
    if (this.oktaAuth.token.isLoginRedirect()) {
      const { tokens } = await this.oktaAuth.token.parseFromUrl()
      this.oktaAuth.tokenManager.setTokens(tokens)
    }
    this.oktaAuth.start() // will update auth state and call event listeners

    const refreshToken = this.getShouldRefreshToken()
    if (refreshToken) {
      await this.refreshToken()
    }
  }

  public getToken = async (initialize: boolean = false): Promise<string> => {
    try {
      const response = await this.oktaAuth.tokenManager.get('accessToken')
      if (response && isAccessToken(response)) { // Use the type guard isAccessToken
        const expirationInMs = response.expiresAt * 1000
        const currentTime = new Date().getTime()
        const tokenValidity = expirationInMs - currentTime
  
        localStorage.setItem('TokenExpiration', `${expirationInMs}`)
        localStorage.setItem('TokenValidity', `${tokenValidity}`)
  
        return response.accessToken
      } else {
        throw new Error('Invalid access token')
      }
    } catch (error) {
      console.error('getToken Error:', error)
      if (initialize) {
        await this.signIn()
      } else {
        EventBus.dispatch('SESSION_EXPIRED')
      }
      return ''
    }
  }

  public signIn = async (): Promise<void> => {
    try {
      // Check if the user is already authenticated
      const isAuthenticated = await this.oktaAuth.isAuthenticated()
      if (!isAuthenticated) {
        // Initiate the sign-in process
        this.oktaAuth.signInWithRedirect()
      }
    } catch (error) {
      console.error("Error during sign-in:", error)
    }
  }  

  public signOut = async (): Promise<void> => {
    console.debug('calling Auth.signOut()')
    localStorage.removeItem('TokenExpiration')
    localStorage.removeItem('TokenValidity')
    await this.oktaAuth.signOut()
  }

  public refreshToken = async (): Promise<void> => {
    localStorage.setItem('RefreshAuthentication', 'true')
    await this.signOut()
  }

  public getShouldRefreshToken = (): boolean => {
    const tokenExpiration = parseInt(localStorage.getItem('TokenExpiration') ?? '0')
    const tokenValidity = parseInt(localStorage.getItem('TokenValidity') ?? '0')
    const currentTime = new Date().getTime()

    if (tokenExpiration === 0) {
      return false
    }

    return currentTime >= tokenExpiration - tokenValidity / 2
  }
}
